import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Check, ArrowRight, BookMarked, BookOpen, Bookmark, CalendarRange } from "lucide-react";

const ORDER = [
  "The Law (Pentateuch)", "History", "Wisdom & Poetry", "Major Prophets", "Minor Prophets",
  "Gospels", "Pauline Epistles", "General Epistles", "Apocalyptic",
];

export default function Scripture() {
  const [books, setBooks] = useState([]);
  const [read, setRead] = useState([]);
  const [position, setPosition] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    api.get("/bible/books").then((r) => setBooks(r.data));
    api.get("/bible/read").then((r) => setRead(r.data));
    api.get("/bible/position").then((r) => setPosition(r.data));
    api.get("/bible/bookmarks").then((r) => setBookmarks(r.data));
  }, []);

  const grouped = useMemo(() => {
    const out = { Old: {}, New: {} };
    books.forEach((b) => {
      out[b.testament][b.category] = out[b.testament][b.category] || [];
      out[b.testament][b.category].push(b);
    });
    return out;
  }, [books]);

  const readSet = new Set(read);
  const pct = books.length ? Math.round((read.length / books.length) * 100) : 0;

  const renderTestament = (label, sub, groups) => (
    <section className="mt-14">
      <div className="flex items-baseline gap-4 mb-8">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">{label}</h2>
        <span className="text-sm text-muted-foreground">{sub}</span>
      </div>
      {ORDER.filter((c) => groups[c]).map((cat) => (
        <div key={cat} className="mb-10">
          <p className="label-overline mb-4">{cat}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {groups[cat].map((b) => {
              const done = readSet.has(b.slug);
              return (
                <Link key={b.slug} to={`/scripture/${b.slug}`} data-testid={`book-card-${b.slug}`}
                  className="group flex items-center justify-between bg-card border border-border rounded-md px-5 py-4 hover:bg-muted/40 transition-colors duration-300">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${done ? "bg-primary text-primary-foreground" : "border border-border text-transparent"}`}>
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-heading text-xl leading-none truncate">{b.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{b.chapters} chapters</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all duration-300 shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="animate-fade-up">
        <p className="label-overline mb-2">Guided Walk Through Scripture</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none flex items-center gap-3">
          <BookMarked className="w-8 h-8 text-accent" /> The Books of the Bible
        </h1>
        <p className="text-muted-foreground mt-4 max-w-xl leading-relaxed">
          Journey through all 66 books — their story, themes, and a key verse for each. Mark each one as you explore it and watch your understanding deepen.
        </p>

        <div className="bg-card border border-border rounded-md p-6 mt-8" data-testid="scripture-progress">
          <div className="flex items-center justify-between mb-3">
            <p className="label-overline">Your progress</p>
            <p className="font-heading text-2xl">
              {books.length === 0
                ? <span className="text-muted-foreground text-base">Loading…</span>
                : <>{read.length} <span className="text-muted-foreground text-base">/ {books.length} explored</span></>}
            </p>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {position?.slug && (
            <Link to={`/scripture/${position.slug}/read?chapter=${position.chapter}`} data-testid="continue-reading-card"
              className="group bg-primary text-primary-foreground rounded-md p-6 hover:bg-primary/90 transition-colors duration-300 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <BookOpen className="w-6 h-6 text-accent" />
                <div>
                  <p className="label-overline text-primary-foreground/60">Continue reading</p>
                  <p className="font-heading text-2xl leading-tight mt-0.5">{position.book} {position.chapter}</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          )}
          <Link to="/plans" data-testid="reading-plans-card"
            className="group bg-card border border-border rounded-md p-6 hover:bg-muted/40 transition-colors duration-300 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CalendarRange className="w-6 h-6 text-accent" />
              <div>
                <p className="label-overline">Reading plans</p>
                <p className="font-heading text-2xl leading-tight mt-0.5">Follow a journey</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-all duration-300" />
          </Link>
        </div>

        {bookmarks.length > 0 && (
          <div className="mt-8" data-testid="bookmarks-section">
            <p className="label-overline mb-3 flex items-center gap-2"><Bookmark className="w-3.5 h-3.5 text-accent" /> Your bookmarks</p>
            <div className="flex flex-wrap gap-2">
              {bookmarks.map((b) => (
                <Link key={`${b.slug}-${b.chapter}`} to={`/scripture/${b.slug}/read?chapter=${b.chapter}`}
                  data-testid={`bookmark-chip-${b.slug}-${b.chapter}`}
                  className="text-sm px-4 py-1.5 rounded-full bg-muted text-secondary-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                  {b.book} {b.chapter}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {renderTestament("Old Testament", "39 books", grouped.Old)}
      {renderTestament("New Testament", "27 books", grouped.New)}
    </div>
  );
}
